import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Reveal } from '../hooks/useReveal';
import { christmasContent, christmasImage } from '../christmas';
import { Snowfall, Garland, Ornament, StarDivider } from '../components/ChristmasDecor';
import { ChristmasVideos } from '../components/ChristmasVideos';

const Heading = ({ eyebrow, title, subtitle }) => (
  <div className="text-center max-w-3xl mx-auto">
    {eyebrow && <span className="font-sans-el text-[12px] tracking-[0.35em] uppercase text-[#e6b83f]">{eyebrow}</span>}
    <h2 className="font-deco text-3xl sm:text-4xl text-[#f7f1e3] mt-3">{title}</h2>
    <StarDivider />
    {subtitle && <p className="font-serif-el text-base md:text-lg text-[#c9c2b3]">{subtitle}</p>}
  </div>
);

const Hero = ({ c }) => (
  <section className="relative overflow-hidden bg-[#0f0d09] pt-36 pb-20 px-6" data-testid="xmas-hero">
    <Snowfall />
    <Ornament className="left-[6%] top-24 hidden md:flex" color="#b3262e" size={44} line={70} />
    <Ornament className="right-[8%] top-24 hidden md:flex" color="#e6b83f" size={56} line={110} />
    <Ornament className="right-[22%] top-24 hidden lg:flex" color="#2f6b47" size={34} line={40} />
    <div className="relative max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
      <Reveal>
        <span className="font-sans-el text-[12px] tracking-[0.35em] uppercase text-[#e6b83f]">{c.eyebrow}</span>
        <h1 className="font-deco text-4xl sm:text-5xl lg:text-6xl font-bold gold-gradient-text mt-4 leading-tight" data-testid="xmas-title">{c.title}</h1>
        <p className="font-serif-el text-lg text-[#c9c2b3] mt-6 leading-relaxed">{c.subtitle}</p>
        <div className="flex flex-wrap gap-4 mt-9">
          <Link to="/#contact" data-testid="xmas-hero-book-btn" className="btn-gold px-7 py-3.5 rounded-sm text-[13px] tracking-[0.2em] uppercase font-medium">{c.cta}</Link>
          <a href="#xmas-videos" data-testid="xmas-hero-videos-btn" className="btn-ghost-gold px-7 py-3.5 rounded-sm text-[13px] tracking-[0.2em] uppercase">{c.ctaVideos}</a>
        </div>
      </Reveal>
      <Reveal delay={150}>
        <div className="relative max-w-md mx-auto">
          <div className="absolute -top-4 -left-4 w-24 h-24 border-t border-l border-[#e6b83f]/60" />
          <div className="absolute -bottom-4 -right-4 w-24 h-24 border-b border-r border-[#e6b83f]/60" />
          <img src={christmasImage} alt="LAJO Navidad plaza comercial" data-testid="xmas-hero-image" className="w-full h-auto block" />
        </div>
      </Reveal>
    </div>
  </section>
);

const Events = ({ c }) => (
  <section className="section-pad bg-[#14120d]">
    <div className="max-w-6xl mx-auto">
      <Reveal><Heading title={c.eventsTitle} subtitle={c.eventsSubtitle} /></Reveal>
      <div className="grid md:grid-cols-2 gap-6 mt-14">
        {c.events.map((e, i) => {
          const Icon = Icons[e.icon] || Icons.Star;
          return (
            <Reveal key={e.title} delay={i * 90}>
              <div className="card-lux p-8 h-full" data-testid={`xmas-event-${i}`}>
                <span className="w-12 h-12 rounded-full border border-[#e6b83f]/50 flex items-center justify-center text-[#e6b83f]"><Icon size={22} strokeWidth={1.5} /></span>
                <h3 className="font-deco text-2xl text-[#f7f1e3] mt-5">{e.title}</h3>
                <div className="gold-line w-16 my-4" />
                <p className="font-sans-el text-[16px] text-[#cbc4b3] font-light leading-relaxed">{e.desc}</p>
              </div>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

const Repertoire = ({ c }) => (
  <section className="section-pad bg-[#0f0d09] relative overflow-hidden">
    <Snowfall />
    <div className="relative max-w-5xl mx-auto">
      <Reveal><Heading title={c.whyTitle} /></Reveal>
      <div className="grid sm:grid-cols-3 gap-6 mt-12">
        {c.why.map((w) => (
          <Reveal key={w.v}><div className="text-center card-lux p-7">
            <div className="font-deco text-4xl gold-gradient-text">{w.k}</div>
            <p className="font-sans-el text-[14px] text-[#cbc4b3] mt-3">{w.v}</p>
          </div></Reveal>
        ))}
      </div>
      <Reveal><h3 className="font-sans-el text-[12px] tracking-[0.35em] uppercase text-[#e6b83f] text-center mt-16 mb-6">{c.repTitle}</h3></Reveal>
      <div className="flex flex-wrap justify-center gap-3" data-testid="xmas-repertoire">
        {c.repertoire.map((s) => (
          <span key={s} className="font-serif-el text-[15px] text-[#f7eccf] border border-[#e6b83f]/30 px-4 py-2 rounded-full hover:border-[#e6b83f] hover:bg-[#e6b83f]/10 transition-colors">{s}</span>
        ))}
      </div>
    </div>
  </section>
);

const ChristmasPage = () => {
  const { lang } = useLanguage();
  const c = christmasContent[lang];
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <main data-testid="christmas-page">
      <Hero c={c} />
      <Garland className="bg-[#14120d]" />
      <Events c={c} />
      <section id="xmas-videos" className="section-pad bg-[#14120d] pt-0">
        <div className="max-w-6xl mx-auto">
          <Reveal><Heading eyebrow="LAJO" title={c.videosTitle} subtitle={c.videosSubtitle} /></Reveal>
          <div className="mt-14"><ChristmasVideos lang={lang} /></div>
        </div>
      </section>
      <Repertoire c={c} />
      <Garland className="bg-[#14120d]" />
      <section className="section-pad bg-[#14120d] text-center">
        <Reveal>
          <Heading title={c.closingTitle} subtitle={c.closingText} />
          <Link to="/#contact" data-testid="xmas-closing-book-btn" className="btn-gold inline-block mt-8 px-8 py-4 rounded-sm text-[13px] tracking-[0.2em] uppercase font-medium">{c.cta}</Link>
        </Reveal>
      </section>
    </main>
  );
};

export default ChristmasPage;
